import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart } from '../../../../types/Chart/Chart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';

import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { NavForward } from '../../../../components/buttons/NavForward';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { FieldInfo } from '../../../../components/FieldInfo';
import { FormSelect } from '../../../../components/forms/FormSelect';

import * as Sentry from '@sentry/browser';
import { formatDateForCalendar } from '../../../../utilities/formatDateForCalendar';
import { formatDateForStorage } from '../../../../utilities/formatDateForStorage';
import { formatDateForDisplay } from '../../../../utilities/formatDateForDisplay';

import { differenceInYears } from 'date-fns';

export const Route = createFileRoute('/_authenticated/$patientId/info/patientInfo')({
  component: PatientInfoPage,
});

function PatientInfoPage() {
  const { mutate } = useUpdateChartSection();
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [prevMarked, setPrevMarked] = React.useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
  const [alerted, setAlerted] = React.useState(false);
  const [hasGender, setHasGender] = React.useState(false);

  // INITIALIZE FORM
  const patientInfoForm = useForm({
    defaultValues: {
      firstName: chart?.PATIENT_INFO_1?.firstName ? chart.PATIENT_INFO_1?.firstName : '',
      middleInitial: chart?.PATIENT_INFO_1?.middleInitial ? chart.PATIENT_INFO_1?.middleInitial : '',
      lastName: chart?.PATIENT_INFO_1?.lastName ? chart.PATIENT_INFO_1?.lastName : '',
      gender: chart?.PATIENT_INFO_1?.gender ? chart.PATIENT_INFO_1.gender : '',
      DOB: chart?.PATIENT_INFO_1?.DOB ? formatDateForCalendar(chart.PATIENT_INFO_1?.DOB) : '',
      selfReporting: chart?.PATIENT_INFO_1?.selfReporting === false ? 'false' : 'true', // Default to true if not set
      cellPhone: chart?.PATIENT_INFO_2?.cellPhone ? chart.PATIENT_INFO_2?.cellPhone : '',
      email: chart?.PATIENT_INFO_2?.email
        ? chart.PATIENT_INFO_2?.email
        : patientInfo?.contactEmail
          ? patientInfo.contactEmail
          : '',
      city: chart?.PATIENT_INFO_2?.city ? chart.PATIENT_INFO_2?.city : '',
      state: chart?.PATIENT_INFO_2?.state ? chart.PATIENT_INFO_2?.state : '',
      ZIP: chart?.PATIENT_INFO_2?.ZIP ? chart.PATIENT_INFO_2?.ZIP : '',
      country: chart?.PATIENT_INFO_2?.country ? chart.PATIENT_INFO_2?.country : 'USA',
      streetAddress: chart?.PATIENT_INFO_2?.streetAddress ? chart.PATIENT_INFO_2?.streetAddress : '',
      homePhone: chart?.PATIENT_INFO_2?.homePhone ? chart.PATIENT_INFO_2?.homePhone : '',
    },
    onSubmit: () => {
      handleIdInfoUpdate();
    },
  });

  // SET INITIAL FOCUS ON CELL PHONE INPUT
  React.useEffect(() => {
    const cellPhoneInput = document.getElementById('cell-phone-input');
    if (cellPhoneInput) {
      cellPhoneInput.focus();
    }
  });

  // SET PATIENT HAS GENDER
  React.useEffect(() => {
    if (chart?.PATIENT_INFO_1.gender?.length > 0) {
      setHasGender(true);
    }
  }, [chart]);

  // ALERT PATIENT TO CURRENT STATE OF SCANNED DL INFO
  React.useEffect(() => {
    const dlHasInfo = () => {
      if (!chart || !chart.PATIENT_INFO_1 || !chart.PATIENT_INFO_2) {
        return false;
      } else if (
        chart.PATIENT_INFO_1.firstName?.length > 0 ||
        (chart.PATIENT_INFO_1.middleInitial && chart.PATIENT_INFO_1.middleInitial?.length > 0) ||
        chart.PATIENT_INFO_1.lastName?.length > 0 ||
        chart.PATIENT_INFO_1.DOB?.toString().length > 0 ||
        chart.PATIENT_INFO_2.streetAddress.length > 0 ||
        chart.PATIENT_INFO_2.city.length > 0 ||
        chart.PATIENT_INFO_2.state.length > 0 ||
        chart.PATIENT_INFO_2.ZIP.length > 0
      ) {
        return true;
      } else {
        return false;
      }
    };

    if (chart?.PHOTO_ID?.photoIdentificationFrontURI && dlHasInfo() && !alerted) {
      setAlerted(true);
      setTimeout(() => {
        alert(`We were able to read the following information from your driver's license:
        Name: ${chart.PATIENT_INFO_1.firstName ? chart.PATIENT_INFO_1.firstName : ''} ${chart.PATIENT_INFO_1.middleInitial ? chart.PATIENT_INFO_1.middleInitial : ''} ${chart.PATIENT_INFO_1.lastName ? chart.PATIENT_INFO_1.lastName : ''}
        DOB: ${chart.PATIENT_INFO_1.DOB.seconds ? formatDateForDisplay(chart.PATIENT_INFO_1.DOB) : ''}
        Address: ${chart.PATIENT_INFO_2.streetAddress}
        City: ${chart.PATIENT_INFO_2.city}
        State: ${chart.PATIENT_INFO_2.state}
        Zip: ${chart.PATIENT_INFO_2.ZIP}
        If any of this information is incorrect or incomplete, please correct it on this page.`);
      }, 2000);
    }
  }, [chart, alerted]);

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj && !prevMarked) {
      markPreviousPageCompleted({
        currentPageName: 'patientInfo',
        patientInfo,
        value: true,
      });
      setPrevMarked(true);
    }
  }, [patientInfo, prevMarked]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo?.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'patientInfo',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  // WRITE FORM DATA TO FIRESTORE
  const handleIdInfoUpdate = () => {
    handleIdInfo1Update();
    handleIdInfo2Update();
  };
  const handleIdInfo1Update = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          PATIENT_INFO_1: {
            firstName: patientInfoForm.state.values.firstName,
            middleInitial: patientInfoForm.state.values.middleInitial,
            lastName: patientInfoForm.state.values.lastName,
            gender: patientInfoForm.state.values.gender,
            DOB: patientInfoForm.state.values.DOB
              ? formatDateForStorage(patientInfoForm.state.values.DOB)
              : formatDateForStorage(new Date().toLocaleDateString('en-US')),
            selfReporting: patientInfoForm.state.values.selfReporting === 'true' ? true : false, // Convert string to boolean
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  const handleIdInfo2Update = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          PATIENT_INFO_2: {
            cellPhone: patientInfoForm.state.values.cellPhone,
            country: patientInfoForm.state.values.country,
            email: patientInfoForm.state.values.email,
            city: patientInfoForm.state.values.city,
            state: patientInfoForm.state.values.state,
            ZIP: patientInfoForm.state.values.ZIP,
            streetAddress: patientInfoForm.state.values.streetAddress,
            homePhone: patientInfoForm.state.values.homePhone,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          setIsSubmitted(true);
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId) {
    return (
      <>
        {/* <Header patientId={patientId} /> */}
        <div id="photo-upload-page" className="flex flex-col items-center">
          <h1 className="place-self-center text-3xl font-bold">PATIENT INFORMATION</h1>
          <div className="my-4">
            <div id="front-id-container" className="flex flex-col items-center">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  patientInfoForm.handleSubmit();
                }}
              >
                <div className="flex flex-col">
                  <patientInfoForm.Field
                    name="selfReporting"
                    children={(field) => (
                      <div className="w-1/2">
                        <FormSelect
                          label="Who is filling out this form?*"
                          name="selfReporting"
                          id="self-reporting-select"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                          options={[
                            { name: 'The patient', value: 'true' },
                            { name: "The patient's legally appointed health care agent.", value: 'false' },
                          ]}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </div>
                    )}
                  />
                  <patientInfoForm.Field
                    name="firstName"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'First name cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="First Name*"
                          id="first-name"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="middleInitial"
                    children={(field) => (
                      <>
                        <FormInput
                          label="Middle Initial"
                          id="middle-initial"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={false}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="lastName"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Last name cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Last Name*"
                          id="last-name"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="gender"
                    listeners={{
                      onChange: ({ value }) => {
                        if (value && value.length > 0) {
                          setHasGender(true);
                        }
                      },
                      onMount: ({ value }) => {
                        if (value && value.length > 0) {
                          setHasGender(true);
                        }
                      },
                    }}
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Please select a gender above';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <div className="w-1/3">
                        <FormSelect
                          label="Gender*"
                          name="gender"
                          id="gender-select"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                          options={[
                            { name: '--Please choose an option--', value: '' },
                            { name: 'Male', value: 'male' },
                            { name: 'Female', value: 'female' },
                            { name: 'Non-Binary', value: 'non-binary' },
                          ]}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </div>
                    )}
                  />
                  <patientInfoForm.Field
                    name="DOB"
                    validators={{
                      onChange: ({ value }) => {
                        if (differenceInYears(Date.now(), new Date(value)) < 18) {
                          return '*Patient must be at least 18 years old to receive services';
                        }
                        if (!value) {
                          return 'Date of Birth is invalid';
                        }
                        return undefined;
                      },
                    }}
                    children={(field) => (
                      <div className="scheme-dark">
                        <FormInput
                          label="DOB* (MM/DD/YYYY)"
                          id="dob"
                          type="date"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <p className="text-xl font-bold text-green-900 italic">
                          *Please confirm patient DOB matches government ID before submitting.
                        </p>
                        <FieldInfo fieldMeta={field.state.meta} />
                      </div>
                    )}
                  />

                  <div className="my-2">
                    <h2 className="text-xl font-bold">Cell Phone*</h2>
                    <patientInfoForm.Field
                      name="cellPhone"
                      validators={{
                        onChange: ({ value }) => {
                          if (value && value.length < 2) {
                            return 'Cell Phone cannot be blank';
                          } else if (value && value.length < 11) {
                            return 'Cell Phone must contain at least 10 digits';
                          } else {
                            return undefined;
                          }
                        },
                      }}
                      children={(field) => (
                        <>
                          <PhoneInput
                            country={'us'}
                            inputStyle={{
                              backgroundColor: '#374151',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '1.125rem',
                              lineHeight: '1.75rem',
                            }}
                            inputProps={{
                              id: 'cell-phone-input',
                              name: 'cellPhone',
                              required: true,
                              autoFocus: true,
                            }}
                            placeholder="Enter phone number"
                            value={field.state.value}
                            onChange={(value) => {
                              if (value) {
                                field.setValue(value);
                              }
                            }}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                  </div>
                  <div className="my-2">
                    <h2 className="text-xl font-bold">Home Phone (Optional)</h2>
                    <patientInfoForm.Field
                      name="homePhone"
                      children={(field) => (
                        <>
                          <PhoneInput
                            country={'us'}
                            inputStyle={{ backgroundColor: '#374151', color: 'white', fontWeight: 'bold' }}
                            inputProps={{
                              name: 'homePhone',
                              required: true,
                              autoFocus: true,
                            }}
                            placeholder="Enter phone number"
                            value={field.state.value}
                            onChange={(value) => {
                              if (value) {
                                field.setValue(value);
                              }
                            }}
                          />
                          <FieldInfo fieldMeta={field.state.meta} />
                        </>
                      )}
                    />
                  </div>
                  <patientInfoForm.Field
                    name="email"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Email cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Email*"
                          id="email"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="streetAddress"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Street address cannot be blank.';
                        } else if (value.length < 5) {
                          return 'Street address must be at least 5 characters';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Street Address*"
                          id="street-address"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="city"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'City cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="City*"
                          id="city"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="state"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'State cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="State*"
                          id="state"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="ZIP"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'ZIP Code cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="ZIP*"
                          id="ZIP"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <patientInfoForm.Field
                    name="country"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Country cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Country*"
                          id="country"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                </div>
                <patientInfoForm.Subscribe
                  selector={(state) => [state.canSubmit, state.isSubmitting]}
                  children={([canSubmit, isSubmitting]) => (
                    <>
                      {!canSubmit && (
                        <div className="mt-4 flex flex-col items-center">
                          <p className="text-red-500">Please correct any errors above before re-submitting.</p>
                        </div>
                      )}
                      <Button
                        id="submit-id-info"
                        type="submit"
                        textContent="Submit"
                        disabled={!hasGender || !canSubmit || isSubmitting}
                        onClick={handleIdInfoUpdate}
                        submitted={isSubmitted}
                      />
                    </>
                  )}
                />
              </form>
            </div>
          </div>
          <div id="nav" className="my-4 flex flex-col items-center">
            {nextPageURL && isSubmitted && <NavForward to={nextPageURL} disabled={!isSubmitted} />}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
